import React, { useState } from 'react';
import EduTip from './EduTip.jsx';
import Select from './Select.jsx';
import Checkbox from './Checkbox.jsx';
import { SEASONAL, LIFESTYLE } from '../data.js';
import { fmtMoney, currentYM } from '../engine.js';

const SET_TITLE = 'm-0 text-[13px] text-[#f1f5f9] font-bold tracking-[0.02em] flex items-center gap-2';
const SET_TITLE_GAP = `${SET_TITLE} mt-3 pt-3 border-t border-dashed border-[#334155]`;
const SET_NOTE = 'm-0 text-[#94a3b8] text-[11px] leading-[1.5]';
const SETROW = 'grid grid-cols-[180px_1fr] gap-[10px] items-center py-1 border-b border-dashed border-[rgba(148,163,184,0.12)] last:border-b-0';
const SETROW_LABEL = 'flex items-center gap-[6px] text-[#cbd5e1] text-[12px]';
const SETROW_CONTROL = 'text-[12px]';
const SETROW_HINT = 'col-start-2 text-[#64748b] text-[10px] italic';
const NUM_INPUT = 'bg-surface-raised border border-border-strong text-text px-[6px] py-[2px] rounded-[3px] font-[inherit] text-[0.8rem]';
const HINT = 'mt-[0.7rem] text-[0.80rem] text-text-muted px-3 py-[0.55rem] bg-surface-el border-l-[3px] border-border-strong rounded-sm';
const BTN_GHOST_SM = 'bg-transparent text-accent-blue border border-dashed border-border-strong px-2 py-[2px] rounded-sm font-[inherit] text-[11px] cursor-pointer hover:border-accent-blue hover:bg-[rgba(96,165,250,0.08)]';

function multOpt() {
  return [0, 0.25, 0.5, 0.75, 1.0, 1.1, 1.25, 1.5, 2.0];
}

function effectiveAmount(event, year, overrides) {
  const o = overrides?.[event.id]?.[String(year)];
  if (o?.customAmount != null) return Number(o.customAmount);
  if (o?.multiplier != null)   return Number(event.amount) * Number(o.multiplier);
  return event.amount;
}

function OccurrenceOverrideRow({ event, scenario, setScenario, baseYear, span = 3 }) {
  const overrides = scenario.eventOverrides || {};
  const eventOverrides = overrides[event.id] || {};
  const years = Array.from({ length: span }, (_, i) => baseYear + i - 1);

  const setOverride = (year, patch) => {
    const nextEvt = { ...eventOverrides };
    const yKey = String(year);
    const existing = nextEvt[yKey] || {};
    const merged = { ...existing, ...patch };
    if (merged.customAmount == null && merged.multiplier == null) {
      delete nextEvt[yKey];
    } else {
      nextEvt[yKey] = merged;
    }
    const nextAll = { ...overrides };
    if (Object.keys(nextEvt).length === 0) delete nextAll[event.id];
    else nextAll[event.id] = nextEvt;
    setScenario({ ...scenario, eventOverrides: nextAll });
  };

  const th = 'text-left text-text-dim font-medium px-[0.4rem] py-[0.3rem] border-b border-border';
  const td = 'px-[0.4rem] py-[0.35rem] border-b border-dashed border-border';

  return (
    <div className="px-[0.7rem] py-[0.6rem] bg-surface border border-border rounded-sm">
      <div className="flex justify-between items-baseline mb-[0.4rem]">
        <strong className="text-text">{event.label}</strong>
        <em className="text-[0.75rem] text-text-muted not-italic">base {fmtMoney(event.amount, { decimals: 2 })}</em>
      </div>
      <div className="overflow-x-auto">
      <table className="w-full border-collapse text-[0.82rem]">
        <thead>
          <tr>
            <th className={th}>Year</th>
            <th className={th}>Multiplier</th>
            <th className={th}>Custom amount</th>
            <th className={th}>Effective</th>
          </tr>
        </thead>
        <tbody>
          {years.map((y) => {
            const o = eventOverrides[String(y)] || {};
            const eff = effectiveAmount(event, y, overrides);
            return (
              <tr key={y}>
                <td className={td}><strong>{y}</strong></td>
                <td className={td}>
                  <Select
                    compact
                    value={o.multiplier ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setOverride(y, { multiplier: v === '' ? null : parseFloat(v) });
                    }}
                  >
                    <option value="">(none)</option>
                    {multOpt().map((m) => <option key={m} value={m}>×{m.toFixed(2)}</option>)}
                  </Select>
                </td>
                <td className={td}>
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="—"
                    className={NUM_INPUT}
                    value={o.customAmount ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setOverride(y, { customAmount: v === '' ? null : parseFloat(v) });
                    }}
                    style={{ width: 110 }}
                  />
                </td>
                <td className={td}><strong>{fmtMoney(eff, { decimals: 2 })}</strong></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
      <p className={HINT}>Override resolution: <em className="not-italic">customAmount</em> wins over <em className="not-italic">multiplier</em> wins over base.</p>
    </div>
  );
}

export default function SeasonalEditor({ scenario, setScenario }) {
  const set = (patch) => setScenario(s => ({ ...s, ...patch }));
  const [openOverrides, setOpenOverrides] = useState({});
  const baseYear = parseInt(currentYM().split('-')[0], 10);
  const overrides = scenario.eventOverrides || {};

  const seasonalAnnual = SEASONAL.events
    .filter(e => SEASONAL.categoriesEnabled[e.type])
    .reduce((s, e) => s + effectiveAmount(e, baseYear, overrides), 0);
  const lifestyleAnnual = LIFESTYLE.events
    .filter(e => LIFESTYLE.categoriesEnabled[e.type])
    .reduce((s, e) => s + effectiveAmount(e, baseYear, overrides), 0);

  const overrideCount = Object.values(overrides).reduce(
    (s, evt) => s + Object.keys(evt).length, 0
  );

  const toggleOverride = (id) => setOpenOverrides(o => ({ ...o, [id]: !o[id] }));

  const tblCls = 'w-full mt-[6px] text-[10px] border-collapse';
  const seasTh = 'text-left text-[#94a3b8] font-semibold tracking-[0.04em] uppercase border-b border-[#334155] px-[6px] py-1 text-[9px]';
  const seasTd = 'px-[6px] py-[6px] border-b border-dashed border-[rgba(148,163,184,0.12)] text-[#cbd5e1] align-top';

  const renderEventTable = (events) => (
    <div className="overflow-x-auto">
    <table className={tblCls}>
      <thead>
        <tr>
          <th className={seasTh}>Event</th>
          <th className={seasTh}>Month</th>
          <th className={seasTh}>Type</th>
          <th className={`${seasTh} text-right`}>{baseYear} amount</th>
          <th className={seasTh}></th>
        </tr>
      </thead>
      <tbody>
        {events.map((e) => {
          const eff = effectiveAmount(e, baseYear, overrides);
          const overridden = eff !== e.amount;
          const isOpen = !!openOverrides[e.id];
          return (
            <React.Fragment key={e.id}>
              <tr className={overridden ? 'bg-[rgba(240,180,41,0.05)]' : ''}>
                <td className={seasTd}>
                  <strong>{e.label}</strong><br />
                  <em className="block text-[#64748b] text-[9px] not-italic mt-[2px]">{e.note}</em>
                </td>
                <td className={seasTd}>{e.month === null ? 'Any' : ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][e.month - 1]}</td>
                <td className={seasTd}>{e.type}</td>
                <td className={`${seasTd} text-right tabular-nums`}>
                  {fmtMoney(eff, { decimals: 2 })}
                  {overridden && <em className="inline-block px-1 text-[0.85em] text-text-muted opacity-70 not-italic" title={`Base ${fmtMoney(e.amount, { decimals: 2 })}`}> ★</em>}
                </td>
                <td className={seasTd}>
                  <button
                    type="button"
                    className={BTN_GHOST_SM}
                    onClick={() => toggleOverride(e.id)}
                  >
                    {isOpen ? '▾ Override' : '▸ Override'}
                  </button>
                </td>
              </tr>
              {isOpen && (
                <tr>
                  <td colSpan={5} className="bg-surface-el">
                    <OccurrenceOverrideRow
                      event={e}
                      scenario={scenario}
                      setScenario={setScenario}
                      baseYear={baseYear}
                      span={3}
                    />
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
    </div>
  );

  const detailsCls = 'mt-[6px] bg-[rgba(15,23,42,0.4)] border border-[#334155] rounded-[8px] px-3 py-[6px] text-[11px]';
  const summaryCls = 'cursor-pointer text-[#cbd5e1] py-1 font-semibold hover:text-[#93c5fd]';

  return (
    <div className="flex flex-col gap-[10px]">
      <h4 className={SET_TITLE}>Seasonal & Occasional — off-recurring spend</h4>
      <p className={SET_NOTE}>
        Patterns not captured in recurring transactions: birthdays, holidays, year-end events,
        community sponsorships, team milestones, local celebrations, and other occasional spend.
        {overrideCount > 0 && <strong className="text-text"> · {overrideCount} per-occurrence override{overrideCount === 1 ? '' : 's'} active.</strong>}
      </p>
      <div className={SETROW}>
        <div className={SETROW_LABEL}>Enable seasonal</div>
        <div className={SETROW_CONTROL}>
          <Checkbox checked={!!scenario.seasonalEnabled}
                    onChange={(v) => set({ seasonalEnabled: v })} />
        </div>
      </div>
      <div className={SETROW}>
        <div className={SETROW_LABEL}>Seasonal multiplier</div>
        <div className={SETROW_CONTROL}>
          <Select
            compact
            value={scenario.seasonalMultiplier ?? 1}
            onChange={(e) => set({ seasonalMultiplier: parseFloat(e.target.value) })}
          >
            {multOpt().map(v => <option key={v} value={v}>{`×${v.toFixed(2)}`}</option>)}
          </Select>
        </div>
        <div className={SETROW_HINT}>Annual seasonal at ×1.0 = {fmtMoney(seasonalAnnual, { decimals: 2 })} ({baseYear})</div>
      </div>

      <details className={detailsCls}>
        <summary className={summaryCls}>Line items ({SEASONAL.events.length}) — click "Override" to set per-year amounts</summary>
        {renderEventTable(SEASONAL.events)}
      </details>

      <h4 className={SET_TITLE_GAP}>
        Active Lifestyle — Hyrox, races, supplements
      </h4>
      <div className={SETROW}>
        <div className={SETROW_LABEL}>Enable lifestyle</div>
        <div className={SETROW_CONTROL}>
          <Checkbox checked={!!scenario.lifestyleEnabled}
                    onChange={(v) => set({ lifestyleEnabled: v })} />
        </div>
      </div>
      <div className={SETROW}>
        <div className={SETROW_LABEL}>Lifestyle multiplier</div>
        <div className={SETROW_CONTROL}>
          <Select
            compact
            value={scenario.lifestyleMultiplier ?? 1}
            onChange={(e) => set({ lifestyleMultiplier: parseFloat(e.target.value) })}
          >
            {multOpt().map(v => <option key={v} value={v}>{`×${v.toFixed(2)}`}</option>)}
          </Select>
        </div>
        <div className={SETROW_HINT}>Annual lifestyle at ×1.0 = {fmtMoney(lifestyleAnnual, { decimals: 2 })} ({baseYear})</div>
      </div>

      <details className={detailsCls}>
        <summary className={summaryCls}>Line items ({LIFESTYLE.events.length}) — click "Override" to set per-year amounts</summary>
        {renderEventTable(LIFESTYLE.events)}
      </details>
    </div>
  );
}
